import React, { useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../utils/apiHelper';
import html2canvas from 'html2canvas';
import GiftCardForm from './GiftCardCreate/GiftCardForm';
import GiftCardPreview from './GiftCardCreate/GiftCardPreview';

const GiftCardCreate = ({ onCreated }) => {
  // Estado del formulario (sin monto, que se calcula)
  const [createData, setCreateData] = useState({
    expires_at: '',
    cliente_id: '',
    metodo_pago: 'Efectivo',
    destinatario_nombre: '',
    servicios: []  // Servicios seleccionados
  });
  
  const [clients, setClients] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [createdGiftCard, setCreatedGiftCard] = useState(null);
  const giftCardRef = useRef(null);

  // Cargar clientes
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchWithAuth('/clientes');
        setClients(data);
      } catch (err) {
        console.error('Error al obtener clientes:', err);
      }
    })();
  }, []);

  // Cargar servicios
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchWithAuth('/services');
        setAvailableServices(data);
      } catch (err) {
        console.error('Error al obtener servicios:', err);
      }
    })();
  }, []);

  const today = new Date().toISOString().split('T')[0];

  const handleFieldChange = () => {
    if (createdGiftCard) {
      setCreatedGiftCard(null);
    }
  };

  const handleCreateGiftCard = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Validaciones obligatorias
    if (!createData.expires_at) {
      setError("La fecha de expiración es obligatoria.");
      return;
    }
    if (!createData.cliente_id) {
      setError("El comprador es obligatorio.");
      return;
    }
    if (!createData.destinatario_nombre) {
      setError("El destinatario es obligatorio.");
      return;
    }
    if (createData.servicios.length === 0) {
      setError("Debe seleccionar al menos un servicio.");
      return;
    }

    setIsLoading(true);
    try {
      const computedTotal = createData.servicios.reduce((acc, s) => acc + Number(s.valor), 0);
      const payload = {
        initial_value: computedTotal,
        expires_at: createData.expires_at,
        cliente_id: createData.cliente_id || null,
        metodo_pago: createData.metodo_pago,
        destinatario_nombre: createData.destinatario_nombre,
        servicios: createData.servicios  
      };
      const response = await fetchWithAuth('/giftcards', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setMessage(`Gift Card creada. Código: ${response.giftCard.public_code}`);
      // Reiniciar el formulario (manteniendo el comprador seleccionado)
      setCreateData({ 
        expires_at: '', 
        cliente_id: createData.cliente_id, 
        metodo_pago: 'Efectivo',
        destinatario_nombre: '',
        servicios: [] 
      });
      setClientSearchTerm('');
      setActiveSuggestionIndex(-1);
      setCreatedGiftCard(response.giftCard);
      if (onCreated) onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    const fullName = `${client.nombre} ${client.apellido}`.toLowerCase();
    return fullName.includes(clientSearchTerm.toLowerCase());
  });

  const handleSelectClientOption = (client) => {
    setClientSearchTerm(`${client.nombre} ${client.apellido}`);
    setCreateData(prevData => ({ ...prevData, cliente_id: client.cliente_id }));
    setSelectedClient(client);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };

  const handleClientKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredClients.length > 0) {
        setActiveSuggestionIndex((prevIndex) =>
          prevIndex < filteredClients.length - 1 ? prevIndex + 1 : prevIndex
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredClients.length > 0) {
        setActiveSuggestionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : 0
        );
      }
    } else if (e.key === 'Enter') {
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < filteredClients.length) {
        e.preventDefault();
        handleSelectClientOption(filteredClients[activeSuggestionIndex]);
      }
    }
  };

  const displayClientName = () => {
    if (selectedClient) {
      return `${selectedClient.nombre} ${selectedClient.apellido}`;
    } else if (createData.cliente_id) {
      const clientFound = clients.find((c) => c.cliente_id === createData.cliente_id);
      return clientFound ? `${clientFound.nombre} ${clientFound.apellido}` : 'Sin Cliente';
    }
    return 'Sin Cliente';
  };

  const handleDownload = async () => {
    if (!giftCardRef.current) return;
    try {
      const canvas = await html2canvas(giftCardRef.current, { scale: 2 });
      const dataURL = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `GiftCard-${createdGiftCard.public_code}.png`;
      link.href = dataURL;
      link.click();
    } catch (err) {
      console.error('Error generando imagen:', err);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded shadow">
      {message && <div className="mb-4 p-2 bg-green-100 text-green-800 rounded">{message}</div>}
      {error && <div className="mb-4 p-2 bg-red-100 text-red-800 rounded">{error}</div>}
      
      <GiftCardForm
        createData={createData}
        setCreateData={setCreateData}
        clientSearchTerm={clientSearchTerm}
        setClientSearchTerm={setClientSearchTerm}
        filteredClients={filteredClients}
        handleSelectClientOption={handleSelectClientOption}
        handleClientKeyDown={handleClientKeyDown}
        showSuggestions={showSuggestions}
        setShowSuggestions={setShowSuggestions}
        activeSuggestionIndex={activeSuggestionIndex}
        isLoading={isLoading}
        handleCreateGiftCard={handleCreateGiftCard}
        today={today}
        onFieldChange={handleFieldChange}
        availableServices={availableServices}
      />
      
      {createdGiftCard && (
        <GiftCardPreview
          createdGiftCard={createdGiftCard}
          displayClientName={displayClientName}
          giftCardRef={giftCardRef}
          handleDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default GiftCardCreate;
